import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["lenderFilter", "timeWindowFilter", "statusGrid", "statusStatistics", "paceAnalysis", "customDateRange", "startDateInput", "endDateInput"];

  // Corresponds to the STATUS_COLOR_MAP previously in VrMonitoringDataFetcher
  // We'll use this to color the squares on the frontend.
  STATUS_COLOR_MAP = {
    // Success states
    ready: { bg: "bg-green", text: "text-white" }, // Your custom green

    // Error states
    error: { bg: "bg-red", text: "text-white" }, // Your custom red
    error_avm: { bg: "#B00020", text: "text-white" }, // Darker Red (Hex)
    error_lcp: { bg: "bg-pink", text: "text-white" }, // Your custom pink
    error_home_value: { bg: "#D32F2F", text: "text-white" }, // Another distinct Red (Hex)

    // Warning/Missing/Failed states - cycling for variety
    missing_loan_amount: { bg: "bg-orange", text: "text-gray-800" }, // Your custom orange
    missing_loan_term: { bg: "bg-darkOrange", text: "text-white" }, // Your custom darkOrange
    missing_quote_rate: { bg: "bg-gold", text: "text-gray-800" }, // Your custom gold
    missing_ltv: { bg: "#FFA726", text: "text-gray-800" }, // Hex - Bright Orange
    missing_purchase_price: { bg: "#FFB74D", text: "text-gray-800" }, // Hex - Lighter Orange
    rltv_out_of_range: { bg: "#FFC107", text: "text-gray-800" }, // Hex - Amber
    missing_property_value: { bg: "#FFCA28", text: "text-gray-800" }, // Hex - Light Amber
    missing_data_enrichment: { bg: "#FFD54F", text: "text-gray-800" }, // Hex - Very Light Amber
    missing_cherre_lcp: { bg: "#FFEE58", text: "text-gray-800" }, // Hex - Bright Yellow
    failed_homeiq: { bg: "#FDD835", text: "text-gray-800" }, // Hex - Yellow/Gold
    failed_cherre: { bg: "#FBC02D", text: "text-gray-800" }, // Hex - Darker Yellow/Gold
    failed_pmms: { bg: "#FB8C00", text: "text-white" }, // Hex - Deep Orange
    missing_pmms_values_avm: { bg: "#F57C00", text: "text-white" }, // Corrected key from LenderClient enum

    error_property_address: { bg: "bg-yellow-400", text: "text-gray-800" }, // Tailwind default
    invalid_property_address: { bg: "bg-yellow-500", text: "text-gray-800" }, // Tailwind default

    // In-progress/pending states
    pending: { bg: "bg-blue-400", text: "text-white" }, // Tailwind default
    queued: { bg: "bg-blue-300", text: "text-gray-800" }, // Tailwind default (needs dark text)
    processing: { bg: "bg-blue-500", text: "text-white" }, // Tailwind default
    vro_pending: { bg: "bg-purple-400", text: "text-white" }, // Tailwind default

    // Other/neutral states
    not_ready: { bg: "bg-gray", text: "text-white" }, // Your custom gray #6E6E6E
    cancelled: { bg: "bg-grayaltsecond", text: "text-white" }, // Your custom gray #878787
    archived: { bg: "bg-grayalt", text: "text-gray-800" }, // Your custom gray #D9D9D9 (light)
    default: { bg: "bg-graylight", text: "text-gray-800" }, // Your custom gray #F2F6F7 (very light)
  };

  connect() {
    // Load state from URL first
    this.loadStateFromURL();
    
    this.populateLenderFilter();
    this.fetchAndDisplayStatusData(); // Initial fetch
    this.fetchAndDisplayPaceAnalysis(); // Initial fetch

    // Add event listeners to filters to re-fetch data on change
    this.lenderFilterTarget.addEventListener("change", async () => {
      this.updateURL();
      await Promise.all([
        this.fetchAndDisplayStatusData(),
        this.fetchAndDisplayPaceAnalysis()
      ]);
    });
    
    this.timeWindowFilterTarget.addEventListener("change", async () => {
      this.toggleCustomDateRange();
      this.updateURL();
      await Promise.all([
        this.fetchAndDisplayStatusData(),
        this.fetchAndDisplayPaceAnalysis()
      ]);
    });

    // Add event listeners for custom date inputs
    if (this.hasStartDateInputTarget) {
      this.startDateInputTarget.addEventListener("change", async () => {
        this.updateURL();
        await Promise.all([
          this.fetchAndDisplayStatusData(),
          this.fetchAndDisplayPaceAnalysis()
        ]);
      });
    }

    if (this.hasEndDateInputTarget) {
      this.endDateInputTarget.addEventListener("change", async () => {
        this.updateURL();
        await Promise.all([
          this.fetchAndDisplayStatusData(),
          this.fetchAndDisplayPaceAnalysis()
        ]);
      });
    }

    // Handle browser back/forward
    window.addEventListener("popstate", () => {
      this.loadStateFromURL();
      this.fetchAndDisplayStatusData();
      this.fetchAndDisplayPaceAnalysis();
    });
  }

  async populateLenderFilter() {
    try {
      const response = await fetch("/monitoring/value_report_creation/lenders");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const lenders = await response.json();
      // Clear existing options except for "All Lenders"
      while (this.lenderFilterTarget.options.length > 1) {
        this.lenderFilterTarget.remove(1);
      }
      lenders.forEach((lender) => {
        const option = document.createElement("option");
        option.value = lender.id;
        option.textContent = lender.name;
        this.lenderFilterTarget.appendChild(option);
      });
    } catch (error) {
      // Optionally, display an error message to the user in the UI
    }
  }

  async fetchAndDisplayStatusData() {
    const lenderId = this.lenderFilterTarget.value;
    const timeWindowValue = this.timeWindowFilterTarget.value;

    let startTimeISO, endTimeISO;

    if (timeWindowValue === "custom") {
      // Use custom date range
      if (!this.hasStartDateInputTarget || !this.hasEndDateInputTarget || 
          !this.startDateInputTarget.value || !this.endDateInputTarget.value) {
        this.statusGridTarget.innerHTML = '<p class="text-yellow-500">Please select both start and end dates for custom range.</p>';
        return;
      }
      
      const startTime = new Date(this.startDateInputTarget.value);
      const endTime = new Date(this.endDateInputTarget.value);
      
      if (startTime >= endTime) {
        this.statusGridTarget.innerHTML = '<p class="text-red-500">Start date must be before end date.</p>';
        return;
      }
      
      startTimeISO = startTime.toISOString();
      endTimeISO = endTime.toISOString();
    } else {
      // Use predefined time window
      const timeWindowMinutes = parseInt(timeWindowValue);
      const endTime = new Date();
      const startTime = new Date(endTime.getTime() - timeWindowMinutes * 60000);
      
      startTimeISO = startTime.toISOString();
      endTimeISO = endTime.toISOString();
    }

    let url = `/monitoring/value_report_creation/status_data?start_time=${startTimeISO}&end_time=${endTimeISO}`;
    if (lenderId) {
      url += `&lender_id=${lenderId}`;
    }

    this.statusGridTarget.innerHTML =
      '<p class="text-gray-500">Loading VR statuses...</p>'; // Show loading message

    try {
      const response = await fetch(url);
      if (!response.ok) {
        // Attempt to parse error response, provide fallback if parsing fails
        let errorDetails = { error: "Unknown error during fetch" };
        try {
          errorDetails = await response.json();
        } catch (parseError) {
          errorDetails.error = response.statusText || errorDetails.error;
        }
        this.statusGridTarget.innerHTML = `<p class="text-red-500">Error loading data: ${errorDetails.error}</p>`;
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.renderStatusGrid(data);
      if (this.hasStatusStatisticsTarget) {
        this.renderStatusStatistics(data);
      }
    } catch (error) {
      // Avoid overwriting specific error message if it was already set from response.json()
      if (!this.statusGridTarget.innerHTML.includes("Error loading data")) {
        this.statusGridTarget.innerHTML =
          '<p class="text-red-500">Could not fetch VR status data. Please try again.</p>';
      }
    }
  }

  renderStatusGrid(statusData) {
    this.statusGridTarget.innerHTML = ""; // Clear previous content or loading message

    // Handle new data structure - check if we have the wrapper object
    const data = statusData.data || statusData;
    const totalCount = statusData.total_count || (Array.isArray(statusData) ? statusData.length : 0);
    const displayedCount = statusData.displayed_count || (Array.isArray(data) ? data.length : 0);
    const additionalCount = statusData.additional_count || 0;

    if (!data || data.length === 0) {
      this.statusGridTarget.innerHTML =
        '<p class="text-gray-500">No Value Reports found for the selected criteria.</p>';
      return;
    }

    try {
      // Add information header if there are additional records
      if (additionalCount > 0) {
        const infoDiv = document.createElement("div");
        infoDiv.className = "mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md";
        infoDiv.innerHTML = `
          <p class="text-blue-800 text-sm">
            <strong>Showing ${displayedCount.toLocaleString()} of ${totalCount.toLocaleString()} Value Reports</strong>
            (${additionalCount.toLocaleString()} additional records not displayed)
          </p>
        `;
        this.statusGridTarget.appendChild(infoDiv);
      }

      // Create container for the grid
      const gridContainer = document.createElement("div");
      gridContainer.className = "grid grid-cols-10 sm:grid-cols-15 md:grid-cols-20 lg:grid-cols-25 xl:grid-cols-30 gap-1";

      data.forEach((vr) => {
        const square = document.createElement("div");
        const statusStyle =
          this.STATUS_COLOR_MAP[vr.report_status] ||
          this.STATUS_COLOR_MAP.default;

        // Base classes for the square
        square.className = `w-8 h-8 rounded flex items-center justify-center text-xs font-semibold`;

        // Apply background color
        if (statusStyle.bg.startsWith("#")) {
          square.style.backgroundColor = statusStyle.bg;
        } else {
          square.classList.add(statusStyle.bg);
        }

        // Apply text color
        square.classList.add(statusStyle.text);

        const reportIdText = vr.value_report_id
          ? vr.value_report_id
          : "N/A (Value Report not created)";
        const clientNameText = vr.lender_client_name
          ? `${vr.lender_client_name} (LCID: ${vr.lender_client_id})`
          : "N/A";

        square.title = `ID: ${reportIdText}\nClient: ${clientNameText}\nLender: ${
          vr.lender_name
        }\nStatus: ${vr.report_status}\nCreated: ${new Date(
          vr.created_at
        ).toLocaleString()}`;

        // Make the square clickable to navigate to the event details page
        square.classList.add(
          "cursor-pointer",
          "hover:ring-2",
          "hover:ring-purple-500",
          "hover:ring-offset-1"
        );
        square.addEventListener("click", () => {
          // Ensure vr.event_id is available from the data fetcher
          // (it should be, as 'vr_creation_events.id AS event_id')
          if (vr.event_id) {
            window.location.href = `/monitoring/value_report_creation/events/${vr.event_id}`;
          } else {
            // Optionally, provide user feedback that details aren't available
          }
        });

        gridContainer.appendChild(square);
      });

      // Append the grid container to the target
      this.statusGridTarget.appendChild(gridContainer);
    } catch (error) {
      this.statusGridTarget.innerHTML =
        '<p class="text-red-500">Error rendering VR status grid.</p>';
    }
  }

  renderStatusStatistics(statusData) {
    this.statusStatisticsTarget.innerHTML = ""; // Clear loading message

    // Handle new data structure - check if we have the wrapper object
    const data = statusData.data || statusData;

    if (!data || data.length === 0) {
      this.statusStatisticsTarget.innerHTML =
        '<p class="text-gray-500">No data available for statistics.</p>';
      return;
    }

    try {
      // Count statuses
      const statusCounts = {};
      const totalCount = data.length;

      data.forEach((vr) => {
        const status = vr.report_status || 'default';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      // Sort by count descending
      const sortedStatuses = Object.entries(statusCounts)
        .sort(([,a], [,b]) => b - a);

      // Create statistics display
      const statsContainer = document.createElement("div");
      statsContainer.className = "space-y-2";

      // Add total count
      const totalDiv = document.createElement("div");
      totalDiv.className = "text-lg font-semibold text-gray-800 mb-4";
      totalDiv.textContent = `Total Value Reports: ${totalCount}`;
      statsContainer.appendChild(totalDiv);

      // Create a grid for status statistics
      const gridDiv = document.createElement("div");
      gridDiv.className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";

      sortedStatuses.forEach(([status, count]) => {
        const percentage = ((count / totalCount) * 100).toFixed(1);
        const statusStyle = this.STATUS_COLOR_MAP[status] || this.STATUS_COLOR_MAP.default;

        const statusDiv = document.createElement("div");
        statusDiv.className = "flex items-center justify-between p-3 border rounded-lg";

        // Status indicator and name
        const leftDiv = document.createElement("div");
        leftDiv.className = "flex items-center space-x-2";

        const colorBox = document.createElement("div");
        colorBox.className = "w-4 h-4 rounded-sm";
        if (statusStyle.bg.startsWith("#")) {
          colorBox.style.backgroundColor = statusStyle.bg;
        } else {
          colorBox.classList.add(statusStyle.bg);
        }

        const statusName = document.createElement("span");
        statusName.className = "text-sm font-medium text-gray-700";
        statusName.textContent = this.formatStatusName(status);

        leftDiv.appendChild(colorBox);
        leftDiv.appendChild(statusName);

        // Count and percentage
        const rightDiv = document.createElement("div");
        rightDiv.className = "text-right";

        const countSpan = document.createElement("div");
        countSpan.className = "text-lg font-bold text-gray-800";
        countSpan.textContent = count;

        const percentageSpan = document.createElement("div");
        percentageSpan.className = "text-sm text-gray-500";
        percentageSpan.textContent = `${percentage}%`;

        rightDiv.appendChild(countSpan);
        rightDiv.appendChild(percentageSpan);

        statusDiv.appendChild(leftDiv);
        statusDiv.appendChild(rightDiv);
        gridDiv.appendChild(statusDiv);
      });

      statsContainer.appendChild(gridDiv);
      this.statusStatisticsTarget.appendChild(statsContainer);

    } catch (error) {
      this.statusStatisticsTarget.innerHTML =
        '<p class="text-red-500">Error rendering statistics.</p>';
    }
  }

  formatStatusName(status) {
    // Convert snake_case to readable format
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  toggleCustomDateRange() {
    const isCustom = this.timeWindowFilterTarget.value === "custom";
    
    if (this.hasCustomDateRangeTarget) {
      this.customDateRangeTargets.forEach(target => {
        if (isCustom) {
          target.classList.remove("hidden");
        } else {
          target.classList.add("hidden");
        }
      });
    }

    // Set default values for custom range if just selected
    if (isCustom && this.hasStartDateInputTarget && this.hasEndDateInputTarget) {
      if (!this.startDateInputTarget.value || !this.endDateInputTarget.value) {
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        
        // Format to datetime-local format (YYYY-MM-DDTHH:MM)
        this.endDateInputTarget.value = this.formatDateTimeLocal(now);
        this.startDateInputTarget.value = this.formatDateTimeLocal(oneHourAgo);
      }
    }
  }

  formatDateTimeLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  updateURL() {
    const params = new URLSearchParams();
    
    // Add lender filter
    const lenderId = this.lenderFilterTarget.value;
    if (lenderId) {
      params.set('lender_id', lenderId);
    }
    
    // Add time window
    const timeWindow = this.timeWindowFilterTarget.value;
    params.set('time_window', timeWindow);
    
    // Add custom dates if applicable
    if (timeWindow === "custom" && this.hasStartDateInputTarget && this.hasEndDateInputTarget) {
      const startDate = this.startDateInputTarget.value;
      const endDate = this.endDateInputTarget.value;
      
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
    }
    
    // Update URL without triggering page reload
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }

  loadStateFromURL() {
    const params = new URLSearchParams(window.location.search);
    
    // Load lender filter
    const lenderId = params.get('lender_id');
    if (lenderId) {
      this.lenderFilterTarget.value = lenderId;
    }
    
    // Load time window
    const timeWindow = params.get('time_window');
    if (timeWindow) {
      this.timeWindowFilterTarget.value = timeWindow;
      this.toggleCustomDateRange();
    }
    
    // Load custom dates
    const startDate = params.get('start_date');
    const endDate = params.get('end_date');
    
    if (startDate && this.hasStartDateInputTarget) {
      this.startDateInputTarget.value = startDate;
    }
    
    if (endDate && this.hasEndDateInputTarget) {
      this.endDateInputTarget.value = endDate;
    }
  }

  async fetchAndDisplayPaceAnalysis() {
    const lenderId = this.lenderFilterTarget.value;
    let url = '/monitoring/value_report_creation/pace_analysis';
    
    if (lenderId) {
      url += `?lender_id=${lenderId}`;
    }

    if (this.hasPaceAnalysisTarget) {
      this.paceAnalysisTarget.innerHTML = '<p class="text-gray-500">Loading pace analysis...</p>';
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        let errorDetails = { error: "Unknown error during fetch" };
        try {
          errorDetails = await response.json();
        } catch (parseError) {
          errorDetails.error = response.statusText || errorDetails.error;
        }
        
        if (this.hasPaceAnalysisTarget) {
          this.paceAnalysisTarget.innerHTML = `<p class="text-red-500">Error loading pace analysis: ${errorDetails.error}</p>`;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.renderPaceAnalysis(data);
    } catch (error) {
      if (this.hasPaceAnalysisTarget && !this.paceAnalysisTarget.innerHTML.includes("Error loading pace analysis")) {
        this.paceAnalysisTarget.innerHTML = '<p class="text-red-500">Could not fetch pace analysis data. Please try again.</p>';
      }
    }
  }

  renderPaceAnalysis(data) {
    if (!this.hasPaceAnalysisTarget) return;

    this.paceAnalysisTarget.innerHTML = "";

    if (!data.pace_metrics || data.pace_metrics.length === 0) {
      const noDataDiv = document.createElement("div");
      noDataDiv.className = "text-center py-8";
      noDataDiv.innerHTML = `
        <div class="text-gray-500 mb-2">
          <svg class="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
          <p class="text-lg font-medium">No pace data available</p>
          <p class="text-sm">${data.analysis_note || 'No Value Reports found for analysis.'}</p>
        </div>
      `;
      this.paceAnalysisTarget.appendChild(noDataDiv);
      return;
    }

    // Lender info and analysis note
    const headerDiv = document.createElement("div");
    headerDiv.className = "mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200";
    headerDiv.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold text-blue-900">
          ${data.lender_info.name}
        </h3>
        <span class="text-sm text-blue-600 font-medium">Creation Rate Analysis</span>
      </div>
      <p class="text-sm text-blue-700">${data.analysis_note}</p>
    `;
    this.paceAnalysisTarget.appendChild(headerDiv);

    // Pace metrics grid
    const gridDiv = document.createElement("div");
    gridDiv.className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";

    data.pace_metrics.forEach((metric) => {
      const metricCard = document.createElement("div");
      metricCard.className = "bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200";
      
      const rateColor = this.getRateColor(metric.rate_per_minute);
      
      metricCard.innerHTML = `
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-semibold text-gray-800 text-sm">${metric.name}</h4>
          <div class="w-3 h-3 rounded-full ${rateColor.bg}"></div>
        </div>
        
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-xs text-gray-500">Created:</span>
            <span class="font-bold text-lg ${rateColor.text}">${metric.vr_count}</span>
          </div>
          
          <div class="flex justify-between items-center">
            <span class="text-xs text-gray-500">Rate:</span>
            <span class="font-semibold text-sm ${rateColor.text}">${metric.rate_per_minute}/min</span>
          </div>
          
          <div class="pt-2 border-t border-gray-100">
            <div class="text-xs text-gray-400">
              ${this.formatElapsedTime(metric.minutes_elapsed)} elapsed
            </div>
          </div>
        </div>
      `;
      
      gridDiv.appendChild(metricCard);
    });

    this.paceAnalysisTarget.appendChild(gridDiv);
  }

  getRateColor(rate) {
    if (rate >= 100) {
      return { bg: "bg-green-500", text: "text-green-600" };
    } else if (rate >= 50) {
      return { bg: "bg-blue-500", text: "text-blue-600" };
    } else if (rate >= 20) {
      return { bg: "bg-yellow-500", text: "text-yellow-600" };
    } else if (rate >= 5) {
      return { bg: "bg-orange-500", text: "text-orange-600" };
    } else {
      return { bg: "bg-red-500", text: "text-red-600" };
    }
  }

  formatElapsedTime(minutes) {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    } else if (minutes < 1440) {
      const hours = Math.round(minutes / 60 * 10) / 10;
      return `${hours} hr`;
    } else {
      const days = Math.round(minutes / 1440 * 10) / 10;
      return `${days} days`;
    }
  }
}
